import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { brandColors } from '../../../../theme/theme';
import { TrendingDown } from '@mui/icons-material';

/**
 * ConversionFlowChart Component
 * 
 * Sankey-style diagram showing audience flow:
 * - Aware → splits to Interest/Not Interest
 * - Interest → splits to Theater/Streaming
 * - Theater → splits to Opening/Later
 * 
 * Visual representation of leakage points
 */
const ConversionFlowChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for conversion flow analysis
        </Typography>
      </Paper>
    );
  }

  // Calculate overall totals
  const totals = data.reduce((acc, record) => ({
    totalResponses: acc.totalResponses + (record.TotalResponses || 0),
    totalAware: acc.totalAware + (record.TotalAware || 0),
    totalInterest: acc.totalInterest + (record.TotalInterest || 0),
    totalTheater: acc.totalTheater + (record.TotalTheater || 0),
    totalWillingToPay: acc.totalWillingToPay + (record.TotalWillingToPay || 0),
    totalAwareOpeningWeekend: acc.totalAwareOpeningWeekend + (record.TotalAwareOpeningWeekend || 0),
    totalExcessAwareness: acc.totalExcessAwareness + (record.TotalExcessAwareness || 0)
  }), {
    totalResponses: 0,
    totalAware: 0,
    totalInterest: 0,
    totalTheater: 0,
    totalWillingToPay: 0,
    totalAwareOpeningWeekend: 0,
    totalExcessAwareness: 0
  });

  // Calculate flow data
  const notAware = totals.totalResponses - totals.totalAware;
  const awareNoInterest = totals.totalExcessAwareness; // Aware but low interest
  const interestedNoTheater = totals.totalInterest - totals.totalTheater;
  const theaterNoOpening = totals.totalTheater - totals.totalAwareOpeningWeekend;

  // Calculate conversion rates
  const conversionRates = {
    awarenessRate: totals.totalResponses > 0 ? (totals.totalAware / totals.totalResponses) * 100 : 0,
    interestRate: totals.totalAware > 0 ? (totals.totalInterest / totals.totalAware) * 100 : 0,
    theaterRate: totals.totalInterest > 0 ? (totals.totalTheater / totals.totalInterest) * 100 : 0,
    openingRate: totals.totalTheater > 0 ? (totals.totalAwareOpeningWeekend / totals.totalTheater) * 100 : 0
  };

  // Calculate drop-offs
  const dropOffs = {
    notAware: { count: notAware, pct: totals.totalResponses > 0 ? (notAware / totals.totalResponses) * 100 : 0 },
    noInterest: { count: awareNoInterest, pct: totals.totalAware > 0 ? (awareNoInterest / totals.totalAware) * 100 : 0 },
    noTheater: { count: interestedNoTheater, pct: totals.totalInterest > 0 ? (interestedNoTheater / totals.totalInterest) * 100 : 0 },
    noOpening: { count: theaterNoOpening, pct: totals.totalTheater > 0 ? (theaterNoOpening / totals.totalTheater) * 100 : 0 }
  };

  // Custom label for bars
  const renderLabel = (props) => {
    const { x, y, width, height, value } = props;
    
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="white"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="12"
        fontWeight="600"
      >
        {value.toLocaleString()}
      </text>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Audience Conversion Flow
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track audience progression and identify drop-off points
        </Typography>
      </Box>

      {/* Conversion Rate Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.primary}40`, backgroundColor: `${brandColors.primary}05` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Awareness Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.primary }}>
                {conversionRates.awarenessRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totals.totalAware.toLocaleString()} of {totals.totalResponses.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.secondary}40`, backgroundColor: `${brandColors.secondary}05` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Interest Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.secondary }}>
                {conversionRates.interestRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totals.totalInterest.toLocaleString()} of {totals.totalAware.toLocaleString()} aware
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', border: `2px solid #2196f340`, backgroundColor: `#2196f305` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Theater Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
                {conversionRates.theaterRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totals.totalTheater.toLocaleString()} of {totals.totalInterest.toLocaleString()} interested
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.success}40`, backgroundColor: `${brandColors.success}05` }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                Opening Weekend Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.success }}>
                {conversionRates.openingRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {totals.totalAwareOpeningWeekend.toLocaleString()} of {totals.totalTheater.toLocaleString()} theater
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Flow Visualization */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Conversion Flow Stages
        </Typography>
        <Grid container spacing={2}>
          {/* Stage 1: Total to Aware */}
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', textAlign: 'center' }}>
                Total Surveyed → Aware
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[{
                  name: 'Total',
                  aware: totals.totalAware,
                  notAware: notAware
                }]}>
                  <XAxis dataKey="name" />
                  <YAxis hide />
                  <Tooltip />
                  <Bar dataKey="aware" stackId="a" fill={brandColors.primary}>
                    <LabelList content={renderLabel} />
                  </Bar>
                  <Bar dataKey="notAware" stackId="a" fill="#e0e0e0">
                    <LabelList content={renderLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.primary }}>
                  ✓ {conversionRates.awarenessRate.toFixed(1)}% convert
                </Typography>
                <br />
                <Typography variant="caption" sx={{ color: brandColors.error }}>
                  ✗ {dropOffs.notAware.pct.toFixed(1)}% drop ({dropOffs.notAware.count.toLocaleString()})
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Stage 2: Aware to Interested */}
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', textAlign: 'center' }}>
                Aware → Interested
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[{
                  name: 'Aware',
                  interested: totals.totalInterest,
                  noInterest: awareNoInterest
                }]}>
                  <XAxis dataKey="name" />
                  <YAxis hide />
                  <Tooltip />
                  <Bar dataKey="interested" stackId="a" fill={brandColors.secondary}>
                    <LabelList content={renderLabel} />
                  </Bar>
                  <Bar dataKey="noInterest" stackId="a" fill="#e0e0e0">
                    <LabelList content={renderLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.secondary }}>
                  ✓ {conversionRates.interestRate.toFixed(1)}% convert
                </Typography>
                <br />
                <Typography variant="caption" sx={{ color: brandColors.error }}>
                  ✗ {dropOffs.noInterest.pct.toFixed(1)}% drop ({dropOffs.noInterest.count.toLocaleString()})
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Stage 3: Interested to Theater */}
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', textAlign: 'center' }}>
                Interested → Theater
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[{
                  name: 'Interest',
                  theater: totals.totalTheater,
                  streaming: interestedNoTheater
                }]}>
                  <XAxis dataKey="name" />
                  <YAxis hide />
                  <Tooltip />
                  <Bar dataKey="theater" stackId="a" fill="#2196f3">
                    <LabelList content={renderLabel} />
                  </Bar>
                  <Bar dataKey="streaming" stackId="a" fill="#e0e0e0">
                    <LabelList content={renderLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#2196f3' }}>
                  ✓ {conversionRates.theaterRate.toFixed(1)}% convert
                </Typography>
                <br />
                <Typography variant="caption" sx={{ color: brandColors.error }}>
                  ✗ {dropOffs.noTheater.pct.toFixed(1)}% drop ({dropOffs.noTheater.count.toLocaleString()})
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Stage 4: Theater to Opening Weekend */}
          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 1, display: 'block', textAlign: 'center' }}>
                Theater → Opening Weekend
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[{
                  name: 'Theater',
                  opening: totals.totalAwareOpeningWeekend,
                  later: theaterNoOpening
                }]}>
                  <XAxis dataKey="name" />
                  <YAxis hide />
                  <Tooltip />
                  <Bar dataKey="opening" stackId="a" fill={brandColors.success}>
                    <LabelList content={renderLabel} />
                  </Bar>
                  <Bar dataKey="later" stackId="a" fill="#e0e0e0">
                    <LabelList content={renderLabel} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: brandColors.success }}>
                  ✓ {conversionRates.openingRate.toFixed(1)}% opening
                </Typography>
                <br />
                <Typography variant="caption" sx={{ color: brandColors.info }}>
                  ⏱ {dropOffs.noOpening.pct.toFixed(1)}% later ({dropOffs.noOpening.count.toLocaleString()})
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Leakage Analysis */}
      <Box sx={{ p: 2, backgroundColor: `${brandColors.warning}10`, borderRadius: 2, border: `1px solid ${brandColors.warning}40` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <TrendingDown sx={{ color: brandColors.warning }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: brandColors.warning }}>
            Key Drop-Off Points
          </Typography>
        </Box>
        <Typography variant="body2">
          The largest audience drop-offs occur at:
          {' '}
          <strong>
            {dropOffs.notAware.pct > dropOffs.noInterest.pct && dropOffs.notAware.pct > dropOffs.noTheater.pct
              ? 'Awareness stage'
              : dropOffs.noInterest.pct > dropOffs.noTheater.pct
              ? 'Interest stage'
              : 'Theater conversion stage'}
          </strong>
          {' '}
          ({Math.max(dropOffs.notAware.pct, dropOffs.noInterest.pct, dropOffs.noTheater.pct).toFixed(1)}% drop-off). 
          Focus marketing efforts here for maximum impact.
        </Typography>
      </Box>
    </Paper>
  );
};

export default ConversionFlowChart;

